/**
 * Product Service
 *
 * API layer for the product catalog. Replaces serviceOptionService.
 *
 * TWO SURFACES, mirroring the server:
 *   /api/products                          — the org catalog (owns the product)
 *   /api/meeting-types/:id/products        — attachments (what a meeting type offers)
 *
 * The old API conflated them: creating a "service option" created the priced
 * item AND bound it to one meeting type forever. `createAndAttach` below keeps
 * that one-step experience where the UI wants it, without the binding.
 */
import axios from 'axios';
import api from './api';
import {
  Product,
  ProductImage,
  ProductForMeetingType,
  CreateProductData,
  UpdateProductData,
  AttachmentData,
  ServiceProvider,
  PublicProduct,
  PublicProvider,
} from '../types/product';

const API_URL = `${process.env.REACT_APP_API_URL}/api`;

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const authConfig = () => ({ headers: getAuthHeaders(), withCredentials: true });

// ========== CATALOG ==========

export interface ListProductsParams {
  kind?: string;
  includeInactive?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ListProductsResult {
  products: Product[];
  total: number;
  limit: number;
  offset: number;
}

/** The catalog is paginated — never assume one call returns everything. */
export const listProducts = async (
  params: ListProductsParams = {}
): Promise<ListProductsResult> => {
  const response = await axios.get(`${API_URL}/products`, {
    params,
    ...authConfig(),
  });
  return response.data;
};

export const getProduct = async (productId: number): Promise<Product> => {
  const response = await axios.get(`${API_URL}/products/${productId}`, authConfig());
  return response.data;
};

export const createProduct = async (data: CreateProductData): Promise<Product> => {
  const response = await axios.post(`${API_URL}/products`, data, authConfig());
  return response.data;
};

export const updateProduct = async (
  productId: number,
  data: UpdateProductData
): Promise<Product> => {
  const response = await axios.put(`${API_URL}/products/${productId}`, data, authConfig());
  return response.data;
};

/** Soft delete — the product leaves the catalog, past bookings keep resolving. */
export const deleteProduct = async (productId: number): Promise<void> => {
  await axios.delete(`${API_URL}/products/${productId}`, authConfig());
};

/**
 * Duplicate. `titleSuffix` is passed from the client so it can be localized
 * (「のコピー」 / " (copy)") — the server does not know the viewer's language.
 */
export const duplicateProduct = async (
  productId: number,
  titleSuffix: string
): Promise<Product> => {
  const response = await axios.post(
    `${API_URL}/products/${productId}/duplicate`,
    { titleSuffix },
    authConfig()
  );
  return response.data;
};

// ========== WHAT UNLOCKS A MEETING TYPE ==========

export interface MeetingTypeEntitlements {
  grantedProductIds: number[];
  /** Everything that could unlock it — plans and point packs. */
  products: Product[];
}

export const getMeetingTypeEntitlements = async (
  meetingTypeId: number
): Promise<MeetingTypeEntitlements> => {
  const response = await axios.get(
    `${API_URL}/meeting-types/${meetingTypeId}/products/entitlements`,
    authConfig()
  );
  return {
    grantedProductIds: response.data?.grantedProductIds ?? [],
    products: response.data?.products ?? [],
  };
};

export const setMeetingTypeEntitlements = async (
  meetingTypeId: number,
  productIds: number[]
): Promise<void> => {
  await axios.put(
    `${API_URL}/meeting-types/${meetingTypeId}/products/entitlements`,
    { productIds },
    authConfig()
  );
};

// ========== IMAGES ==========

export const uploadProductImage = async (
  productId: number,
  file: File,
  slot: 'hero' | 'gallery' = 'hero'
): Promise<Product> => {
  const formData = new FormData();
  formData.append('image', file);

  const response = await axios.post(
    `${API_URL}/products/${productId}/images`,
    formData,
    {
      params: { slot },
      withCredentials: true,
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
    }
  );
  return response.data;
};

export const deleteProductImage = async (
  productId: number,
  slot: 'hero' | 'gallery' = 'hero',
  url?: string
): Promise<Product> => {
  const response = await axios.delete(`${API_URL}/products/${productId}/images`, {
    params: { slot, url },
    ...authConfig(),
  });
  return response.data;
};

/**
 * Persist an ordered gallery: upload anything new, then save the final order.
 *
 * Position is the meaning — index 0 becomes the main image and is mirrored into
 * `heroImageUrl` so listings and the booking page keep working unchanged.
 *
 * Uploads run SEQUENTIALLY on purpose. The upload endpoint appends to the
 * gallery and returns the whole product, so the freshly added URL is reliably
 * the last element only while one upload is in flight at a time. Firing them in
 * parallel would scramble which URL belongs to which square.
 */
export const syncProductImages = async (
  productId: number,
  items: Array<{ url?: string; file?: File }>
): Promise<Product> => {
  const ordered: ProductImage[] = [];

  for (const item of items) {
    if (item.url) {
      ordered.push({ url: item.url, sortOrder: ordered.length });
      continue;
    }
    if (!item.file) continue;

    const updated = await uploadProductImage(productId, item.file, 'gallery');
    const gallery = updated.images ?? [];
    const added = gallery[gallery.length - 1];
    if (added?.url) ordered.push({ url: added.url, sortOrder: ordered.length });
  }

  const withOrder = ordered.map((image, index) => ({ ...image, sortOrder: index }));
  return updateProduct(productId, {
    images: withOrder,
    heroImageUrl: withOrder[0]?.url ?? null,
  });
};

export const uploadProductIcon = async (productId: number, file: File): Promise<Product> => {
  const formData = new FormData();
  formData.append('icon', file);

  const response = await axios.post(`${API_URL}/products/${productId}/icon`, formData, {
    withCredentials: true,
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteProductIcon = async (productId: number): Promise<Product> => {
  const response = await axios.delete(`${API_URL}/products/${productId}/icon`, authConfig());
  return response.data;
};

// ========== ATTACHMENTS (per meeting type) ==========

export const getMeetingTypeProducts = async (
  meetingTypeId: number,
  includeInactive = false
): Promise<ProductForMeetingType[]> => {
  const response = await axios.get(
    `${API_URL}/meeting-types/${meetingTypeId}/products`,
    { params: { includeInactive }, ...authConfig() }
  );
  return response.data;
};

export const attachProduct = async (
  meetingTypeId: number,
  productId: number,
  data: AttachmentData = {}
): Promise<void> => {
  await axios.post(
    `${API_URL}/meeting-types/${meetingTypeId}/products`,
    { productId, ...data },
    authConfig()
  );
};

export const updateAttachment = async (
  meetingTypeId: number,
  productId: number,
  data: AttachmentData
): Promise<void> => {
  await axios.put(
    `${API_URL}/meeting-types/${meetingTypeId}/products/${productId}`,
    data,
    authConfig()
  );
};

/** Detach only. The product stays in the catalog for other meeting types. */
export const detachProduct = async (
  meetingTypeId: number,
  productId: number
): Promise<void> => {
  await axios.delete(
    `${API_URL}/meeting-types/${meetingTypeId}/products/${productId}`,
    authConfig()
  );
};

export const reorderProducts = async (
  meetingTypeId: number,
  productIds: number[]
): Promise<void> => {
  await axios.put(
    `${API_URL}/meeting-types/${meetingTypeId}/products/reorder`,
    { productIds },
    authConfig()
  );
};

export const getProductProviders = async (
  meetingTypeId: number,
  productId: number
): Promise<ServiceProvider[]> => {
  const response = await axios.get(
    `${API_URL}/meeting-types/${meetingTypeId}/products/${productId}/providers`,
    authConfig()
  );
  return response.data;
};

/**
 * Create a catalog product and attach it to a meeting type in one call — the
 * "add a service to this meeting type" flow. The product is a normal catalog
 * entry afterwards and can be attached elsewhere.
 */
export const createAndAttach = async (
  meetingTypeId: number,
  data: Omit<CreateProductData, 'meetingTypeIds'>,
  attachment: AttachmentData = {}
): Promise<Product> => {
  // providerUserIds belongs to the attachment, not the catalog entry.
  const { providerUserIds, ...catalogFields } = data;

  const product = await createProduct({
    ...catalogFields,
    kind: data.kind ?? 'service_item',
    meetingTypeIds: [meetingTypeId],
  });

  const attachmentPatch: AttachmentData = { ...attachment };
  if (providerUserIds !== undefined) attachmentPatch.providerUserIds = providerUserIds;

  // `createProduct` already made the attachment with defaults; this only runs
  // when the caller actually has settings to apply.
  if (Object.keys(attachmentPatch).length > 0) {
    await updateAttachment(meetingTypeId, product.id, attachmentPatch);
  }
  return product;
};

// ========== PUBLIC ENDPOINTS ==========

export const getPublicProducts = async (
  username: string,
  slug: string
): Promise<PublicProduct[]> => {
  const response = await axios.get(`${API_URL}/booking/public/${username}/${slug}/services`);
  return response.data;
};

export const getPublicProviders = async (
  username: string,
  slug: string,
  productId?: number
): Promise<PublicProvider[]> => {
  const params = productId ? { serviceId: productId } : {};
  const response = await axios.get(
    `${API_URL}/booking/public/${username}/${slug}/providers`,
    { params }
  );
  return response.data;
};

// ========== PREVIEW MODE ==========
// Preview has a meeting type id but no username/slug, so it reads the
// authenticated endpoint and reshapes it to the public format.

export const getProductsForPreview = async (
  meetingTypeId: number
): Promise<PublicProduct[]> => {
  const products = await getMeetingTypeProducts(meetingTypeId, false);

  return products.map((p): PublicProduct => ({
    id: p.id,
    title: p.title,
    description: p.description,
    durationMinutes: p.durationMinutes,
    price: p.price,
    effectivePrice: p.effectivePrice,
    currency: p.currency,
    heroImageUrl: p.heroImageUrl,
    iconUrl: p.iconUrl,
    iconType: p.iconType,
    iconPreset: p.iconPreset,
    pointCost: p.pointCost,
    providers: p.providers || [],
  }));
};

export const getProvidersForPreview = async (
  meetingTypeId: number,
  productId?: number
): Promise<PublicProvider[]> => {
  const products = await getMeetingTypeProducts(meetingTypeId, false);

  // No products configured — fall back to the meeting type's own hosts.
  if (products.length === 0) {
    const response = await api.get(`/meeting-types/${meetingTypeId}/hosts`);
    const hosts = response.hosts || [];
    return hosts.map((host: any) => ({
      userId: host.userId,
      displayName: host.displayName || host.user?.name || 'Unknown',
      bio: host.bio,
      avatarUrl: host.avatarUrl || host.user?.profileImage,
      additionalFee: parseFloat(host.additionalFee?.toString() || '0'),
    }));
  }

  const providerMap = new Map<number, PublicProvider>();
  products.forEach((product) => {
    if (productId && product.id !== productId) return;
    (product.providers || []).forEach((provider) => {
      if (!providerMap.has(provider.userId)) {
        providerMap.set(provider.userId, {
          userId: provider.userId,
          displayName: provider.displayName,
          bio: provider.bio,
          avatarUrl: provider.avatarUrl,
          additionalFee: provider.additionalFee,
        });
      }
    });
  });

  return Array.from(providerMap.values());
};

const productService = {
  getMeetingTypeEntitlements,
  setMeetingTypeEntitlements,
  listProducts,
  getProduct,
  createProduct,
  updateProduct,
  deleteProduct,
  duplicateProduct,
  uploadProductImage,
  deleteProductImage,
  syncProductImages,
  uploadProductIcon,
  deleteProductIcon,
  getMeetingTypeProducts,
  attachProduct,
  updateAttachment,
  detachProduct,
  reorderProducts,
  getProductProviders,
  createAndAttach,
  getPublicProducts,
  getPublicProviders,
  getProductsForPreview,
  getProvidersForPreview,
};

export default productService;
